import React, { useState, useEffect, useMemo } from 'react'
import {
  RichCardSvg,
  CarouselSvg,
  TextSvg,
  // EyeSvg,
  // ClickSvg,
  // DoubleCheckSvg
} from '../../components/Icons'
import { RichCardStats } from './RichCardStats'
import { CarouselStats } from './CarouselStats'
import { BasicStatsLayout } from './BasicStatsLayout'
import { TextStats } from './TextStats'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import {
  CheckCircleIcon,
  EyeIcon,
  CursorArrowRaysIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/20/solid'

const RICH_CARD_TYPE = 'card'
const CAROUSEL_TYPE = 'carousel'
const TEXT_TYPE = 'text'

const getTrailingIndex = (key) => {
  // Supports keys like:
  // - "init-1/3"
  // - "caixarecobros-dniCheck-1/1"
  const match = String(key).match(/-(\d+)\/(\d+)$/)
  if (!match) return null
  const idx = Number(match[1])
  const total = Number(match[2])
  if (Number.isFinite(idx) && Number.isFinite(total)) return { idx, total }
  return null
}

// Group rbmElements by flow (similar to ConversationalFlow)
// Handles two formats:
// 1. Template format: { "init-1/3": {...}, "init-2/3": {...} } or { "caixarecobros-dniCheck-1/1": {...} }
// 2. Stats format: { "elementName1": {...}, "elementName2": {...} }
const getRbmElements = (rbmElements) => {
  if (!rbmElements || typeof rbmElements !== 'object') {
    console.log('getRbmElements: Invalid input')
    return {}
  }
  
  const rbmElementsCopy = { ...rbmElements }
  delete rbmElementsCopy.rbmFlowAliasesMap
  
  const keys = Object.keys(rbmElementsCopy)
  console.log('getRbmElements: Processing', keys.length, 'keys:', keys.slice(0, 5))
  
  if (keys.length === 0) {
    console.log('getRbmElements: No keys found after filtering')
    return {}
  }
  
  // Check if it's the template format (has keys with pattern ending in "-index/total")
  // Examples: "init-1/3", "caixarecobros-dniCheck-1/1"
  const hasTemplateFormat = keys.some(key => {
    const hasDash = key.includes('-')
    const hasSlash = key.includes('/')
    // Check if it ends with pattern like "-1/3" or "-1/1"
    const endsWithIndexPattern = /-\d+\/\d+$/.test(key)
    return hasDash && hasSlash && endsWithIndexPattern
  })
  
  console.log('getRbmElements: Template format detected:', hasTemplateFormat)
  
  if (hasTemplateFormat) {
    // Template format: group by flow ID (like templates do)
    // For "caixarecobros-dniCheck-1/1", flow ID would be "dniCheck" (second part)
    // For "init-1/3", flow would be "init" (first part, no campaign name)
    const groupedElements = {}
    
    keys.forEach(key => {
      // Extract flow ID - second part after campaign name, or first part if no campaign name
      // For "caixarecobros-dniCheck-1/1" -> flow = "dniCheck"
      // For "init-1/3" -> flow = "init"
      const match = key.match(/^(.+?)(-\d+\/\d+)$/)
      let flow = 'init' // default
      
      if (match) {
        const baseKey = match[1] // e.g., "caixarecobros-dniCheck" or "init"
        const parts = baseKey.split('-')
        
        if (parts.length === 1) {
          // Simple case: "init" -> flow = "init"
          flow = parts[0] || 'init'
        } else if (parts.length >= 2) {
          // Has campaign name: "caixarecobros-dniCheck" -> flow = "dniCheck" (second part)
          flow = parts[1] || parts[0] || 'init'
        } else {
          flow = parts[0] || 'init'
        }
      } else {
        // Fallback: use first part before any dash
        const parts = key.split('-')
        flow = parts.length > 1 ? parts[1] : parts[0] || 'init'
      }
      
      if (!groupedElements[flow]) {
        groupedElements[flow] = []
      }
      
      const element = rbmElementsCopy[key]
      if (element && typeof element === 'object' && !Array.isArray(element)) {
        // Ensure element has name property
        if (!element.name) {
          element.name = key
        }
        groupedElements[flow].push(element)
      }
    })
    
    // Sort each flow's elements by their index
    Object.keys(groupedElements).forEach(flow => {
      groupedElements[flow].sort((a, b) => {
        const aKey = keys.find(k => rbmElementsCopy[k] === a)
        const bKey = keys.find(k => rbmElementsCopy[k] === b)
        const aTrail = aKey ? getTrailingIndex(aKey) : null
        const bTrail = bKey ? getTrailingIndex(bKey) : null
        if (aTrail && bTrail) return aTrail.idx - bTrail.idx
        if (aTrail && !bTrail) return -1
        if (!aTrail && bTrail) return 1
        return String(aKey || '').localeCompare(String(bKey || ''))
      })
    })
    
    console.log('getRbmElements: Grouped into flows:', Object.keys(groupedElements))
    Object.keys(groupedElements).forEach(flow => {
      console.log(`getRbmElements: Flow "${flow}" has ${groupedElements[flow].length} elements`)
    })
    
    return groupedElements
  } else {
    // Stats format: all elements in a single "init" flow
    // The keys are element names, values are element objects
    const elements = []
    
    Object.keys(rbmElementsCopy).forEach(key => {
      const el = rbmElementsCopy[key]
      // Validate element
      if (el && 
          typeof el === 'object' && 
          !Array.isArray(el) &&
          (el.type || el.name)) {
        // Ensure element has a name (use key if not present)
        if (!el.name) {
          el.name = key
        }
        elements.push(el)
      }
    })
    
    console.log('StatisticsRbm: Stats format detected, elements found:', elements.length)
    if (elements.length > 0) {
      console.log('StatisticsRbm: Element names:', elements.map(el => el.name || el.type || 'unknown'))
      // Sort elements by name to maintain consistent order
      elements.sort((a, b) => {
        const aName = a.name || ''
        const bName = b.name || ''
        return aName.localeCompare(bName)
      })
      return { init: elements }
    }
    
    console.log('StatisticsRbm: No valid elements found in stats format')
    // If no valid elements found, return empty
    return {}
  }
}

const getRbmFlowAliasesMap = (rbmElements) => {
  if (rbmElements?.rbmFlowAliasesMap) {
    return rbmElements.rbmFlowAliasesMap
  }
  return {}
}

const getFlowAlias = (flowId, rbmFlowAliasesMap) => {
  if (flowId === 'init') return 'Initial Communication'
  return rbmFlowAliasesMap[flowId] || flowId
}

const getElementIcon = (element) => {
  if (!element) return null
  switch (element.type) {
    case RICH_CARD_TYPE:
      return <RichCardSvg fill="#29275c" stroke="#29275c" />
    case CAROUSEL_TYPE:
      return <CarouselSvg fill="#29275c" stroke="#29275c" />
    case TEXT_TYPE:
      return <TextSvg stroke="#29275c" />
    default:
      return null
  }
}

const renderElement = (element, onNavigateToStep) => {
  if (!element) return <div></div>
  
  switch (element.type) {
    case RICH_CARD_TYPE:
      return (
        <BasicStatsLayout elementInfo={element} onNavigateToStep={onNavigateToStep}>
          <RichCardStats elementInfo={element} onNavigateToStep={onNavigateToStep} />
        </BasicStatsLayout>
      )
    case CAROUSEL_TYPE:
      return (
        <BasicStatsLayout elementInfo={element} onNavigateToStep={onNavigateToStep}>
          <CarouselStats elementInfo={element} onNavigateToStep={onNavigateToStep} />
        </BasicStatsLayout>
      )
    case TEXT_TYPE:
      return (
        <BasicStatsLayout elementInfo={element} onNavigateToStep={onNavigateToStep}>
          <TextStats elementInfo={element} onNavigateToStep={onNavigateToStep} />
        </BasicStatsLayout>
      )
    default:
      return <div></div>
  }
}

const FlowStepCard = ({ element, index, total, onClick, isSelected, dataElementName, ...props }) => {
  const delivered = element?.delivered || 0
  const displayed = element?.displayed || 0
  const clicks = element?.totalClicks ?? element?.clicks ?? 0
  const title = element?.name || `Step ${index + 1}`
  
  return (
    <div
      onClick={onClick}
      className={`relative cursor-pointer transition-all duration-200 rounded-xl ${
        isSelected
          ? 'ring-2 ring-indigo-500 ring-offset-2'
          : 'hover:ring-2 hover:ring-indigo-200 hover:ring-offset-1'
      }`}
      data-element-name={dataElementName}
      {...props}
    >
      <div
        className={`w-full p-3 gap-3 flex items-start border-2 rounded-xl transition-colors overflow-hidden ${
          isSelected
            ? 'border-indigo-500 bg-indigo-50'
            : 'border-gray-200 bg-white hover:bg-gray-50 hover:border-indigo-200'
        }`}
      >
        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-indigo-50 flex-shrink-0">
          {getElementIcon(element)}
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <h3 className="text-sm font-semibold text-grey-blue truncate" title={title}>
            {title}
          </h3>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600 mt-1 tabular-nums">
            <span className="inline-flex items-center gap-1">
              <CheckCircleIcon className="h-4 w-4 text-indigo-600" />
              <span className="font-medium">{delivered}</span>
              <span className="text-gray-500">delivered</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <EyeIcon className="h-4 w-4 text-indigo-600" />
              <span className="font-medium">{displayed}</span>
              <span className="text-gray-500">views</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <CursorArrowRaysIcon className="h-4 w-4 text-indigo-600" />
              <span className="font-medium">{clicks}</span>
              <span className="text-gray-500">clicks</span>
            </span>
          </div>
        </div>
        <div className="text-[11px] text-gray-500 font-semibold flex-shrink-0">
          {index + 1}/{total}
        </div>
      </div>
    </div>
  )
}

const FlowSection = ({
  flowId,
  flowElements,
  flowAlias,
  isInit,
  selectedElement,
  onSelectElement,
  expandedFlows,
  toggleExpanded
}) => {
  const isExpanded = expandedFlows[flowId] !== false
  const hasElements = flowElements && flowElements.length > 0

  if (!hasElements) return null

  return (
    <div
      className={`flex flex-col ${
        !isInit ? 'mt-6 pt-6 border-t-2 border-blue-light/50' : ''
      }`}
    >
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => toggleExpanded(flowId)}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-grey-blue" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-grey-blue" />
          )}
        </button>
        <h2 className="text-xl font-bold text-grey-blue">{flowAlias}</h2>
      </div>

      {isExpanded && (
        <div className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Elements
          </h3>
          <div className="flex flex-col gap-2">
            {flowElements.map((element, index) => (
              <FlowStepCard
                key={`${flowId}-${index}-${element?.name || index}`}
                element={element}
                index={index}
                total={flowElements.length}
                onClick={() => onSelectElement(element)}
                isSelected={
                  selectedElement &&
                  selectedElement.name === element.name &&
                  selectedElement.type === element.type
                }
                dataElementName={element?.name}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export const StatisticsRbm = ({ rbmElements, campaignInfo }) => {
  const [selectedElement, setSelectedElement] = useState(null)
  const [rbmFlows, setRbmFlows] = useState({})
  const [rbmFlowAliasesMap, setRbmFlowAliasesMap] = useState({})
  const [expandedFlows, setExpandedFlows] = useState({ init: true })
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    console.log('=== StatisticsRbm: Processing rbmElements ===')
    console.log('StatisticsRbm: rbmElements received:', rbmElements)
    console.log('StatisticsRbm: rbmElements type:', typeof rbmElements)
    console.log('StatisticsRbm: rbmElements is null?', rbmElements === null)
    console.log('StatisticsRbm: rbmElements is undefined?', rbmElements === undefined)
    
    if (!rbmElements || typeof rbmElements !== 'object') {
      console.log('StatisticsRbm: Invalid rbmElements, resetting state')
      setRbmFlows({})
      setRbmFlowAliasesMap({})
      setSelectedElement(null)
      return
    }

    // Check if it's an empty object
    const allKeys = Object.keys(rbmElements)
    const keys = allKeys.filter(key => key !== 'rbmFlowAliasesMap')
    console.log('StatisticsRbm: All keys:', allKeys)
    console.log('StatisticsRbm: Valid keys (excluding rbmFlowAliasesMap):', keys)
    console.log('StatisticsRbm: Valid keys count:', keys.length)
    
    if (keys.length === 0) {
      console.log('StatisticsRbm: rbmElements is empty object - no valid keys found')
      setRbmFlows({})
      setRbmFlowAliasesMap({})
      setSelectedElement(null)
      return
    }

    // Log sample of first few elements to understand structure
    const sampleKeys = keys.slice(0, 5)
    console.log('StatisticsRbm: Sample keys (first 5):', sampleKeys)
    sampleKeys.forEach(key => {
      const el = rbmElements[key]
      console.log(`StatisticsRbm: Element "${key}":`, {
        type: typeof el,
        isArray: Array.isArray(el),
        hasType: el?.type,
        hasName: el?.name,
        keys: el && typeof el === 'object' ? Object.keys(el) : 'N/A',
        value: el
      })
    })

    const flows = getRbmElements(rbmElements)
    const aliases = getRbmFlowAliasesMap(rbmElements)
    
    console.log('StatisticsRbm: Grouped flows result:', flows)
    console.log('StatisticsRbm: Flow keys:', Object.keys(flows))
    Object.keys(flows).forEach(flowKey => {
      const flowElements = flows[flowKey]
      console.log(`StatisticsRbm: Flow "${flowKey}" has ${flowElements?.length || 0} elements`)
      if (flowElements && flowElements.length > 0) {
        console.log(`StatisticsRbm: Flow "${flowKey}" element names:`, flowElements.map(el => el.name || el.type || 'unknown'))
      }
    })
    
    setRbmFlows(flows)
    setRbmFlowAliasesMap(aliases)

    // Select first element from init flow if available
    if (flows.init && flows.init.length > 0) {
      console.log('StatisticsRbm: Selecting first element from init flow:', flows.init[0])
      setSelectedElement(flows.init[0])
    } else if (Object.keys(flows).length > 0) {
      const firstFlow = Object.keys(flows)[0]
      if (flows[firstFlow] && flows[firstFlow].length > 0) {
        console.log('StatisticsRbm: Selecting first element from flow:', firstFlow, flows[firstFlow][0])
        setSelectedElement(flows[firstFlow][0])
      } else {
        console.log('StatisticsRbm: No elements found in any flow')
        setSelectedElement(null)
      }
    } else {
      console.log('StatisticsRbm: No flows created from rbmElements')
      setSelectedElement(null)
    }
    console.log('=== StatisticsRbm: Processing complete ===')
  }, [rbmElements])

  const toggleExpanded = (flowId) => {
    setExpandedFlows((prev) => ({
      ...prev,
      [flowId]: !prev[flowId]
    }))
  }

  const handleSelectElement = (element) => {
    setSelectedElement(element)
  }

  // Navigate to next step in the same flow (sequential navigation)
  const handleNavigateToNext = () => {
    if (!selectedElement) return
    
    // Find the current element's position in its flow
    let currentFlowId = null
    let currentIndex = -1
    
    Object.entries(rbmFlows).forEach(([flowId, flowElements]) => {
      if (!Array.isArray(flowElements)) return
      
      const index = flowElements.findIndex(el => 
        el?.name === selectedElement.name && el?.type === selectedElement.type
      )
      
      if (index !== -1) {
        currentFlowId = flowId
        currentIndex = index
      }
    })
    
    if (currentFlowId && currentIndex !== -1) {
      const flowElements = rbmFlows[currentFlowId]
      
      // Check if there's a next element in the same flow
      if (currentIndex < flowElements.length - 1) {
        const nextElement = flowElements[currentIndex + 1]
        console.log('[StatisticsRbm] Navigating to next step:', nextElement?.name)
        handleSelectElement(nextElement)
        
        // Scroll to the selected element
        setTimeout(() => {
          const elementCard = document.querySelector(`[data-element-name="${nextElement.name}"]`)
          if (elementCard) {
            elementCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }, 100)
      } else {
        console.log('[StatisticsRbm] No next step in current flow')
      }
    } else {
      console.warn('[StatisticsRbm] Could not find current element in flows')
    }
  }

  // Navigate to next step based on postback
  // This should work like templates: extract the flow ID from postback and navigate to that flow's first element
  const handleNavigateToStep = (suggestionInfo) => {
    if (!suggestionInfo?.postback) {
      console.warn('[StatisticsRbm] No postback in suggestion:', suggestionInfo)
      return
    }
    
    console.log('[StatisticsRbm] Navigating to step from postback:', suggestionInfo.postback)
    console.log('[StatisticsRbm] Available rbmFlows:', Object.keys(rbmFlows))
    console.log('[StatisticsRbm] All element names:', Object.values(rbmFlows).flat().map(el => el?.name).filter(Boolean))
    
    // Clean the postback to extract the element name
    // Format examples:
    // - "caixarecobros-satisfaction-parent:[caixarecobros-sendMail-1/1]-1/1" -> "caixarecobros-satisfaction-1/1"
    // - "caixarecobros-dniCheck-1/1" -> "caixarecobros-dniCheck-1/1"
    let postback = suggestionInfo.postback.trim()
    
    // Remove all metadata patterns using regex
    postback = postback.replace(/-parent:\[[^\]]+\]/g, '')
    postback = postback.replace(/-addFlow:\[[^\]]+\]/g, '')
    postback = postback.replace(/-saveText:\[[^\]]+\]/g, '')
    postback = postback.replace(/-action:\[[^\]]+\]/g, '')
    postback = postback.replace(/-scheduleFlow:\[[^\]]+\]/g, '')
    postback = postback.replace(/-resetAnswer:\[[^\]]+\]/g, '')
    
    // Clean up any double dashes that might result
    postback = postback.replace(/-+/g, '-')
    // Remove trailing dashes
    postback = postback.replace(/-+$/, '')
    
    // Handle cases where we might have duplicate index patterns
    const indexPattern = /-(\d+\/\d+)$/
    const matches = postback.match(indexPattern)
    if (matches) {
      const allMatches = postback.match(/-(\d+\/\d+)/g)
      if (allMatches && allMatches.length > 1) {
        const lastIndex = allMatches[allMatches.length - 1]
        const basePattern = postback.replace(/-(\d+\/\d+)/g, '').replace(/-+$/, '')
        postback = basePattern + lastIndex
      }
    }
    
    console.log('[StatisticsRbm] Original postback:', suggestionInfo.postback)
    console.log('[StatisticsRbm] Cleaned postback:', postback)
    
    // Extract the flow ID from the postback (like templates do)
    // For "caixarecobros-dniCheck-1/1", flow ID would be "dniCheck"
    // For "caixarecobros-satisfaction-1/1", flow ID would be "satisfaction"
    const postbackParts = postback.split('-')
    let targetFlowId = null
    let targetElement = null
    
    if (postbackParts.length >= 3) {
      // Extract flow ID: second part (index 1)
      // "dniCheck-1/1" -> flowId = "dniCheck"
      // "caixarecobros-dniCheck-1/1" -> flowId = "dniCheck"
      targetFlowId = postbackParts[1]
      
      console.log('[StatisticsRbm] Extracted flow ID from postback:', targetFlowId)
      console.log('[StatisticsRbm] Looking for flow in rbmFlows:', Object.keys(rbmFlows))
      
      // Find the flow in rbmFlows by flow ID
      // Flows are grouped by flow ID (e.g., "dniCheck", "satisfaction")
      console.log('[StatisticsRbm] Checking if flow exists:', targetFlowId, 'Available flows:', Object.keys(rbmFlows))
      
      if (rbmFlows[targetFlowId] && Array.isArray(rbmFlows[targetFlowId]) && rbmFlows[targetFlowId].length > 0) {
        // Found the flow! Take the first element (prefer 1/1 if available)
        const flowElements = rbmFlows[targetFlowId]
        // Try to find element with -1/ pattern first, otherwise take first
        targetElement = flowElements.find(el => el?.name?.includes('-1/')) || flowElements[0]
        console.log('[StatisticsRbm] ✅ Found flow directly:', targetFlowId, 'first element:', targetElement?.name)
      } else {
        // Flow not found by ID, try to find by matching element names
        console.log('[StatisticsRbm] Flow not found by ID, searching by element name pattern')
        
        // Search for elements that contain the flow ID pattern
        const flowPattern = `-${targetFlowId}-`
        
        Object.entries(rbmFlows).forEach(([flowId, flowElements]) => {
          if (!Array.isArray(flowElements)) return
          
          flowElements.forEach(el => {
            if (!el?.name) return
            
            // Check if element name contains the flow ID pattern
            if (el.name.includes(flowPattern) || el.name.endsWith(`-${targetFlowId}`)) {
              // Take the first element of this flow (prefer 1/1)
              if (!targetElement || el.name.includes('-1/')) {
                targetElement = el
                targetFlowId = flowId
              }
            }
          })
        })
        
        // If still not found, try exact match with cleaned postback
        if (!targetElement) {
          Object.entries(rbmFlows).forEach(([flowId, flowElements]) => {
            if (!Array.isArray(flowElements)) return
            
            flowElements.forEach(el => {
              if (!el?.name) return
              
              if (el.name === postback) {
                targetElement = el
                targetFlowId = flowId
              }
            })
          })
        }
      }
    } else {
      // Fallback: try exact match
      console.log('[StatisticsRbm] Postback has less than 3 parts, trying exact match')
      Object.entries(rbmFlows).forEach(([flowId, flowElements]) => {
        if (!Array.isArray(flowElements)) return
        
        flowElements.forEach(el => {
          if (!el?.name) return
          
          if (el.name === postback || el.name.includes(postback)) {
            if (!targetElement) {
              targetElement = el
              targetFlowId = flowId
            }
          }
        })
      })
    }
    
    if (targetElement) {
      console.log('[StatisticsRbm] ✅ Found target element:', targetElement.name, 'in flow:', targetFlowId)
      handleSelectElement(targetElement)
      
      // Scroll to the selected element in the sidebar and expand its flow
      setTimeout(() => {
        // Expand the flow that contains this element
        if (targetFlowId) {
          setExpandedFlows(prev => ({ ...prev, [targetFlowId]: true }))
        }
        
        // Scroll to the element card
        const elementCard = document.querySelector(`[data-element-name="${targetElement.name}"]`)
        if (elementCard) {
          elementCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
    } else {
      console.error('[StatisticsRbm] ❌ Could not find element for postback:', {
        originalPostback: suggestionInfo.postback,
        cleanedPostback: postback,
        postbackParts: postback.split('-'),
        availableElements: Object.values(rbmFlows).flat().map(el => el?.name).filter(Boolean),
        rbmFlowsKeys: Object.keys(rbmFlows)
      })
      
      // Try one more time with a more flexible search - look for any element that contains the flow name
      const postbackParts = postback.split('-')
      if (postbackParts.length >= 2) {
        const flowName = postbackParts[1] // e.g., "satisfaction"
        console.log('[StatisticsRbm] Last attempt: searching for flow name:', flowName)
        
        Object.entries(rbmFlows).forEach(([flowId, flowElements]) => {
          if (!Array.isArray(flowElements)) return
          
          flowElements.forEach(el => {
            if (!el?.name) return
            
            // Check if element name contains the flow name
            if (el.name.includes(flowName)) {
              console.log('[StatisticsRbm] Found potential match:', el.name)
              if (!targetElement) {
                targetElement = el
                targetFlowId = flowId
              }
            }
          })
        })
        
        if (targetElement) {
          console.log('[StatisticsRbm] ✅ Found target element (flexible search):', targetElement.name, 'in flow:', targetFlowId)
          handleSelectElement(targetElement)
          
          setTimeout(() => {
            if (targetFlowId) {
              setExpandedFlows(prev => ({ ...prev, [targetFlowId]: true }))
            }
            
            const elementCard = document.querySelector(`[data-element-name="${targetElement.name}"]`)
            if (elementCard) {
              elementCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          }, 100)
        }
      }
    }
  }

  // Filter flows based on search query
  const filteredRbmFlows = useMemo(() => {
    if (!searchQuery.trim()) {
      return rbmFlows
    }
    
    const query = searchQuery.toLowerCase().trim()
    const filtered = {}
    
    Object.keys(rbmFlows).forEach(flowId => {
      const flowElements = rbmFlows[flowId] || []
      const filteredElements = flowElements.filter(element => {
        const elementName = (element?.name || '').toLowerCase()
        return elementName.includes(query)
      })
      
      if (filteredElements.length > 0) {
        filtered[flowId] = filteredElements
      }
    })
    
    return filtered
  }, [rbmFlows, searchQuery])

  // Check if selected element is still in filtered results
  useEffect(() => {
    if (selectedElement && searchQuery.trim()) {
      const isStillVisible = Object.values(filteredRbmFlows).some(flowElements =>
        flowElements.some(el => 
          el.name === selectedElement.name && el.type === selectedElement.type
        )
      )
      
      if (!isStillVisible) {
        // If selected element is filtered out, clear selection
        setSelectedElement(null)
      }
    }
  }, [filteredRbmFlows, searchQuery, selectedElement])

  // Get all flow IDs, with 'init' first
  const flowIds = Object.keys(filteredRbmFlows).sort((a, b) => {
    if (a === 'init') return -1
    if (b === 'init') return 1
    return a.localeCompare(b)
  })

  // Show message if no elements or flows are empty
  const hasElements = rbmElements && 
    typeof rbmElements === 'object' && 
    Object.keys(rbmElements).filter(key => key !== 'rbmFlowAliasesMap').length > 0

  const hasFlows = Object.keys(rbmFlows).length > 0 && 
    Object.values(rbmFlows).some(flow => flow && flow.length > 0)

  if (!hasElements || !hasFlows) {
    const debugInfo = rbmElements ? {
      totalKeys: Object.keys(rbmElements).length,
      validKeys: Object.keys(rbmElements).filter(key => key !== 'rbmFlowAliasesMap').length,
      sampleKeys: Object.keys(rbmElements).slice(0, 3),
      flowsCount: Object.keys(rbmFlows).length,
      flowsWithElements: Object.keys(rbmFlows).filter(flow => rbmFlows[flow]?.length > 0).length
    } : null

    return (
      <article className="flex flex-col w-full">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <p className="text-grey-blue text-lg mb-2 text-center">
            No RBM elements available.
          </p>
          <p className="text-gray-600 text-sm text-center mb-4">
            RBM/RCS messages may not be configured, or the data is still loading.
          </p>
          {debugInfo && (
            <div className="mt-4 p-3 bg-white rounded border border-gray-200 text-xs font-mono">
              <p className="font-semibold mb-2">Debug Info:</p>
              <pre className="whitespace-pre-wrap text-gray-700">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </article>
    )
  }

  return (
    <article className="flex flex-col w-full pb-8">
      <div className="grid grid-cols-1 xl:grid-cols-[380px,1fr] gap-6">
        {/* Left side: Flow steps (sidebar) - NO internal scroll */}
        <aside className="rounded-xl border-2 border-gray-200 bg-white shadow-sm">
          <div className="px-4 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
            <h2 className="text-sm font-semibold text-grey-blue mb-3">Steps</h2>
            {/* Search input */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search step..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          <div className="p-4">
            {flowIds.length > 0 ? (
              flowIds.map((flowId) => (
                <FlowSection
                  key={flowId}
                  flowId={flowId}
                  flowElements={filteredRbmFlows[flowId]}
                  flowAlias={getFlowAlias(flowId, rbmFlowAliasesMap)}
                  isInit={flowId === 'init'}
                  selectedElement={selectedElement}
                  onSelectElement={handleSelectElement}
                  expandedFlows={expandedFlows}
                  toggleExpanded={toggleExpanded}
                />
              ))
            ) : (
              <div className="text-center py-6 text-gray-500">
                <p className="text-sm">No steps found matching "{searchQuery}"</p>
              </div>
            )}
          </div>
        </aside>

        {/* Right side: Selected element stats - STICKY */}
        <section className="min-w-0">
          {selectedElement ? (
            <div className="sticky top-4 rounded-xl border-2 border-gray-200 bg-white shadow-sm p-5">
              <h2 className="text-xl font-semibold text-grey-blue mb-4 truncate">
                {selectedElement.name}
              </h2>
              {renderElement(selectedElement, handleNavigateToStep)}
            </div>
          ) : (
            <div className="sticky top-4 rounded-xl border border-blue-200 bg-blue-50 p-6 text-center">
              <p className="text-grey-blue font-medium">
                Select a step from the left to view its statistics
              </p>
            </div>
          )}
        </section>
      </div>
    </article>
  )
}
